import type { CanvasElement, ChecklistItem as FlatChecklistItem } from '../types/schema'
import { isNoSignInPreviewActive } from '../debug'
import { saveUserGoals } from './timelineStore'
import {
  normalizeFlatBundle,
  transformFlatToNestedTree,
} from '../utils/flatTimelineTransformer'
import { findUndefinedPaths, stripUndefinedDeep } from './syncSanitize'
import { getWrapperRole } from '../utils/wrapperRole'

export { findUndefinedPaths, stripUndefinedDeep } from './syncSanitize'

export const isHelperSyncSession = (role: string | null | undefined): boolean => {
  return role === 'HELPER' || getWrapperRole() === 'helper'
}

export class SyncBridgeEngine {
  private static instance: SyncBridgeEngine
  private lastSyncSignature: string | null = null
  private pendingSync: Promise<void> | null = null

  public static getInstance(): SyncBridgeEngine {
    if (!SyncBridgeEngine.instance) {
      SyncBridgeEngine.instance = new SyncBridgeEngine()
    }
    return SyncBridgeEngine.instance
  }

  /**
   * Translates active Firestore snapshot collections into legacy format and backfills
   * the legacy timeline cache. This method intentionally never writes to `elements`
   * or `checklist_items`, preventing flat-model listener feedback loops.
   */
  public syncFirestoreToLocalCache(
    uid: string | null | undefined,
    elements: CanvasElement[],
    checklistItems: FlatChecklistItem[],
    isHelper?: boolean,
  ): void {
    if (isHelper) return
    if (!uid || !elements || elements.length === 0) return
    if (isNoSignInPreviewActive()) return

    const normalizedBundle = normalizeFlatBundle(elements, checklistItems)
    const nextSignature = JSON.stringify(normalizedBundle)
    if (nextSignature === this.lastSyncSignature) return
    this.lastSyncSignature = nextSignature

    this.pendingSync = Promise.resolve(this.pendingSync)
      .catch(() => undefined)
      .then(async () => {
        try {
          const nestedTree = transformFlatToNestedTree(
            normalizedBundle.elements,
            normalizedBundle.checklistItems,
          )
          if (import.meta.env?.DEV) {
            const undefinedPaths = findUndefinedPaths(nestedTree)
            if (undefinedPaths.length > 0) {
              console.warn(
                '[syncBridgeEngine] Stripped undefined field(s) from tree write:',
                undefinedPaths.slice(0, 10),
                `(and ${Math.max(0, undefinedPaths.length - 10)} more)`,
              )
            }
          }
          const sanitizedTree = stripUndefinedDeep(nestedTree)
          await saveUserGoals(uid, sanitizedTree)
        } catch (error) {
          console.error('[SyncBridgeEngine] Critical failure during data reconciliation loop:', error)
        }
      })
  }
}

export const syncBridgeEngine = SyncBridgeEngine.getInstance()

